import { memo, useEffect } from "react";
import { useDispatch } from "react-redux";

import { EvaSource, TimeStep } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { updateChartByPath } from "store/features";
import { capitalizeFirstLetter } from "utils";

import { useUserEvents } from "hooks";
import { useChartEntities } from "hooks/charts";
import useFeatureFlags from "hooks/useFeatureFlags";

import { TimeScaleSelect, TimeStepIcon } from "components/shared";

import ToolbarButton from "../../../base/ToolbarButton";

const TimeStepToggle = () => {
  const dispatch = useDispatch();
  const { hasFeature } = useFeatureFlags();
  const { id } = useChartsContext();
  const {
    source: sourceState,
    timeStep: timeStepState,
    chartType
  } = useChartEntities(id);
  const { sendChartTimeStepEvent } = useUserEvents();
  const showButton = timeStepState?.show;
  const isEnabled = timeStepState?.enabled ?? true;
  const source = sourceState?.properties?.value;
  const timeStep = timeStepState?.properties?.value;
  // derived state
  const icon = TimeStepIcon[timeStep as TimeStep];
  const label = capitalizeFirstLetter(timeStep);
  function handleTimeStepChange(value: TimeStep) {
    dispatch(
      updateChartByPath({
        id,
        path: `features.timeStep.properties.value`,
        value: value
      })
    );

    sendChartTimeStepEvent({
      chartType: chartType,
      timeStep: value,
      dataSource: source
    });
  }

  // EVA-10: a chart persisted with a Day or Hybrid timestep would otherwise
  // keep requesting daily-resolution data even after "Daily Data Toggles" is
  // disabled and the toggle (the user's only way to change it) is hidden
  // below. Hybrid includes daily-resolution data (see TimeScaleSelect), so it
  // must be reset alongside Day. Force it back to the monthly default.
  const isDailyDataToggleEnabled = hasFeature("Daily Data Toggles");
  useEffect(() => {
    if (
      !isDailyDataToggleEnabled &&
      (timeStep === TimeStep.Day || timeStep === TimeStep.Hybrid)
    ) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.timeStep.properties.value`,
          value: TimeStep.Month
        })
      );
    }
  }, [isDailyDataToggleEnabled, timeStep, dispatch, id]);

  if (!showButton || !isDailyDataToggleEnabled) return null;
  return (
    <TimeScaleSelect
      trigger={["hover"]}
      value={timeStep as TimeStep}
      onChange={handleTimeStepChange}
      isDataSourcePublic={source === EvaSource.Public}
      isDataSourcePrivate={source === EvaSource.Private}
      disabled={!isEnabled}>
      <ToolbarButton
        data-testid="timestep-trigger"
        aria-label={timeStep}
        active={false}
        icon={icon}
        overflowLabel={label}
        onToggle={null}
        disabled={!isEnabled}
      />
    </TimeScaleSelect>
  );
};

export default memo(TimeStepToggle);
